import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Statistics {
  total_api_calls_today: number
  total_api_calls_mtd: number
  unique_consumers: number
  avg_response_time_ms: number
  error_rate_pct: number
  bandwidth_tb: number
}

interface TopEndpoint {
  endpoint: string
  calls_today: number
  avg_latency: number
  error_rate: number
}

interface UsageTrend {
  date: string
  calls: number
  errors: number
  latency_avg: number
}

export default function APIAnalyticsPage() {
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [topEndpoints, setTopEndpoints] = useState<TopEndpoint[]>([])
  const [trends, setTrends] = useState<UsageTrend[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, endpointsRes, trendsRes] = await Promise.all([
          fetch('/api/apianalytics/statistics'),
          fetch('/api/apianalytics/topendpoints'),
          fetch('/api/apianalytics/trends')
        ])
        setStatistics(await statsRes.json())
        setTopEndpoints(await endpointsRes.json())
        setTrends(await trendsRes.json())
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  if (loading) {
    return <div className="p-8"><Header /><div className="mt-8">Loading API Analytics...</div></div>
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">API Analytics</h1>

        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-blue-600">{(statistics.total_api_calls_today / 1000000).toFixed(1)}M</div>
              <div className="text-sm text-gray-500">Calls Today</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-green-600">{(statistics.total_api_calls_mtd / 1000000).toFixed(0)}M</div>
              <div className="text-sm text-gray-500">Calls MTD</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-purple-600">{statistics.unique_consumers}</div>
              <div className="text-sm text-gray-500">Consumers</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-indigo-600">{statistics.avg_response_time_ms}ms</div>
              <div className="text-sm text-gray-500">Avg Response</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-orange-600">{statistics.error_rate_pct}%</div>
              <div className="text-sm text-gray-500">Error Rate</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-teal-600">{statistics.bandwidth_tb}TB</div>
              <div className="text-sm text-gray-500">Bandwidth</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b"><h2 className="text-lg font-semibold">Top Endpoints</h2></div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Endpoint</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Calls Today</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Latency</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Error Rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {topEndpoints.map((ep, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-sm">{ep.endpoint}</td>
                      <td className="px-4 py-3 text-gray-600">{(ep.calls_today / 1000000).toFixed(1)}M</td>
                      <td className="px-4 py-3 text-gray-600">{ep.avg_latency}ms</td>
                      <td className="px-4 py-3 text-gray-600">{ep.error_rate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b"><h2 className="text-lg font-semibold">Usage Trends (7 Days)</h2></div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Calls</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Errors</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Avg Latency</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {trends.map((t, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-600">{t.date}</td>
                      <td className="px-4 py-3 text-gray-600">{(t.calls / 1000000).toFixed(1)}M</td>
                      <td className="px-4 py-3 text-gray-600">{(t.errors / 1000).toFixed(1)}K</td>
                      <td className="px-4 py-3 text-gray-600">{t.latency_avg}ms</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
